// pages/W_HR_R350.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import DataGrid, {
  Column,
  Paging,
  FilterRow,
  HeaderFilter,
  SearchPanel,
  Sorting,
  Export,
  Summary,
  TotalItem,
} from "devextreme-react/data-grid";
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import { Button } from 'devextreme-react/button';
import 'devextreme/dist/css/dx.material.blue.light.css';
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { exportDataGrid } from 'devextreme/excel_exporter';
import API_URL from '../config';

export default function W_HR_R350({ user }) {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [data, setData] = useState([]);
  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [comboLoading, setComboLoading] = useState(false);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formatDate = (date) => {
    if (!date) return '';
    const y = date.getFullYear().toString();
    const m = (date.getMonth() + 1).toString().padStart(2, "0");
    const d = date.getDate().toString().padStart(2, "0");
    return `${y}${m}${d}`;
  };

  const formatOracleDateTimeString = (dateString) => {
    if (!dateString || dateString.trim().length < 14) {
      return dateString;
    }
    const trimmedString = dateString.trim();
    const year = trimmedString.substring(0, 4);
    const month = trimmedString.substring(4, 6);
    const day = trimmedString.substring(6, 8);
    const hour = trimmedString.substring(8, 10);
    const minute = trimmedString.substring(10, 12);
    const second = trimmedString.substring(12, 14);
    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  };

  useEffect(() => {
    (async () => {
      setComboLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API_URL}/api/oracle/cust`);
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        setCustList(res.data.data || []);
      } catch (e) {
        setError(`CUST 로드 오류: ${e.message}`);
      } finally {
        setComboLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (custList.length > 0 && user?.role === "CUST") {
      const code = user.username.trim().toUpperCase();
      const found = custList.find(item => item.CODE_CD.trim().toUpperCase() === code);
      if (found) {
        setSelectedCust(found.CODE_CD);
      } else {
        setError(`사용자 코드(${user.username})가 목록에 없습니다.`);
      }
    }
  }, [custList, user]);

  useEffect(() => {
    if (!selectedCust) {
      setDeviceList([]);
      setSelectedDevice(null);
      return;
    }
    (async () => {
      setDeviceLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API_URL}/api/oracle/device`, { params: { custCd: selectedCust } });
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        setDeviceList(res.data.data || []);
        setSelectedDevice(null);
      } catch (e) {
        setError(`DEVICE 로드 오류: ${e.message}`);
      } finally {
        setDeviceLoading(false);
      }
    })();
  }, [selectedCust]);

  const fetchData = async () => {
    if (!selectedCust) {
      setError("Please select CUST first.");
      return;
    }
    setLoading(true);
    setError("");
    setData([]);

    try {
      const params = {
        inCustCd: selectedCust,
        inDevice: selectedDevice || '%',
        inDateFrom: formatDate(dateFrom),
        inDateTo: formatDate(dateTo)
      };
      const res = await axios.get(`${API_URL}/api/oracle/W_HR_R350`, { params });

      if (res.data.retCode !== 0) throw new Error(res.data.retMsg);

        const mappedData = (res.data.data || []).map((row, idx) => ({
        ...row,
        IN_DATE: formatOracleDateTimeString(row.IN_DATE),
        SHIP_DATE: formatOracleDateTimeString(row.SHIP_DATE),
        id: `${row.LOT_NO}-${row.LOT_SUB || ''}-${idx}`,
      }));
      setData(mappedData);
    } catch (e) {
      const errorMessage = e.response?.data?.retMsg || e.message || "알 수 없는 오류가 발생했습니다.";
      setError(`조회 오류: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("SHIP_LOT_LIST");
    exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true }).then(() => {
      workbook.xlsx.writeBuffer().then(buffer => {
        saveAs(new Blob([buffer], { type: "application/octet-stream" }), "SHIP_LOT_LIST.xlsx");
      });
    });
    e.cancel = true;
  };

  return (
    // [수정] 페이지 전체를 Flexbox 레이아웃으로 설정
    <div style={{ padding: 0, fontFamily: 'Arial, sans-serif', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 1. 검색 영역 */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px' }}>SHIP LOT LIST</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 15, alignItems: "center" }}>
          <DateBox value={dateFrom} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateFrom(e.value)} label="FROM" />
          <DateBox value={dateTo} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateTo(e.value)} label="TO" />
          <SelectBox dataSource={custList} value={selectedCust} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedCust(e.value)} disabled={comboLoading || user?.role === "CUST"} searchEnabled showClearButton={user?.role !== "CUST"} placeholder="Select CUST" label="CUST" />
          <SelectBox dataSource={deviceList} value={selectedDevice} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedDevice(e.value)} disabled={deviceLoading || !selectedCust} searchEnabled showClearButton placeholder="DEVICE (ALL)" label="DEVICE" />
          <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        </div>
        {error && <p style={{ color: "red", fontWeight: 'bold' }}>{error}</p>}
      </div>

      {/* 2. 그리드 영역 (남은 공간을 모두 차지) */}
      <div style={{ flex: 1, position: 'relative' }}> {/* [수정] position: 'relative' 추가 */}
        {/* 로딩 인디케이터를 그리드 영역 안으로 이동 */}
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading...</div>
          </div>
        )}
        <DataGrid
          dataSource={data}
          keyExpr="id"
          onExporting={onExporting}
          height="100%" // [수정] 부모의 높이를 100% 사용
          showBorders={true}
          columnAutoWidth={true}
          allowColumnResizing={true}
          hoverStateEnabled={true}
        >
          <Export enabled={true} allowExportSelectedData={false} />
          <Paging defaultPageSize={20} />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} highlightCaseSensitive={false} />
          <Sorting mode="multiple" />

          <Column dataField="CUST_CD" caption="CUST" />
          <Column dataField="DEVICE" caption="DEVICE" />
          <Column dataField="LOT_NO" caption="LOT NO" />
          <Column dataField="LOT_SUB" caption="LOT SUB" />
          <Column dataField="IN_QTY" caption="IN QTY" dataType="number" alignment="right" />
          <Column dataField="OUT_QTY" caption="OUT QTY" dataType="number" alignment="right" />
          <Column dataField="SHIP_QTY" caption="SHIP QTY" dataType="number" alignment="right" />
          <Column dataField="YEILD" caption="YIELD" dataType="number" alignment="right" />
          <Column dataField="IN_DATE" caption="IN DATE"  />
          <Column dataField="SHIP_DATE" caption="SHIP DATE" />

          <Summary>
            <TotalItem column="LOT_NO" summaryType="count" displayFormat="총 {0} 건" />
            <TotalItem column="IN_QTY" summaryType="sum" valueFormat="#,##0" displayFormat="IN합계: {0}" />
            <TotalItem column="OUT_QTY" summaryType="sum" valueFormat="#,##0" displayFormat="OUT합계: {0}" />
            <TotalItem column="SHIP_QTY" summaryType="sum" valueFormat="#,##0" displayFormat="SHIP합계: {0}" />
          </Summary>
        </DataGrid>
      </div>
    </div>
  );
}